import { Box, makeStyles, useTheme } from '@material-ui/core'
import Quill, { DeltaStatic, RangeStatic, Sources } from 'quill'
import 'quill/dist/quill.snow.css'
import React, { forwardRef, MutableRefObject, useEffect, useLayoutEffect, useRef } from 'react'
import { RichTextEditorProps } from './richTextEditor.model'
import { richTextEditorStyle } from './richTextEditor.styles'

/** RichTextEditor component was built using Quill.js library.
 * It was designed with forwardRef to allow parent component to access the Quill instance
 * and its methods. Also, it leaves the possibility to add new methods later.
 * @props {boolean} readOnly - If true, the editor will be read-only.
 * @props {DeltaStatic} defaultValue - The initial content of the editor.
 * @props {(delta: DeltaStatic, oldContent: DeltaStatic, source: Sources) => void} onTextChange - Callback function that will be called when the text changes.
 * @props {(range: RangeStatic, oldRange: RangeStatic, source: Sources) => void} onSelectionChange - Callback function that will be called when the selection changes.
 *
 */

const RichTextEditor = forwardRef<Quill | null, RichTextEditorProps>(
  ({ readOnly = false, defaultValue, onTextChange, onSelectionChange, height }, ref) => {
    const currentTheme = useTheme()
    const useStyles = makeStyles(richTextEditorStyle(currentTheme))
    const classes = useStyles({ height })

    const containerRef = useRef<HTMLDivElement | null>(null)
    const defaultValueRef = useRef(defaultValue)
    const onTextChangeRef = useRef(onTextChange)
    const onSelectionChangeRef = useRef(onSelectionChange)

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange
      onSelectionChangeRef.current = onSelectionChange
    }, [onTextChange, onSelectionChange])

    useEffect(() => {
      if (ref && 'current' in ref) {
        ref.current?.enable(!readOnly)
      }
    }, [ref, readOnly])

    useEffect(() => {
      const container = containerRef.current
      if (!container) return

      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div') as HTMLDivElement
      )

      const toolbarOptions = [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'], // toggled buttons
        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ align: [] }],
        [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
        [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
        ['blockquote', 'code-block'],
        ['link']
      ]

      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules: {
          toolbar: toolbarOptions
        }
      })

      if (ref && 'current' in ref) {
        ;(ref as MutableRefObject<Quill | null>).current = quill
      }

      if (defaultValueRef.current) {
        quill.setContents(defaultValueRef.current)
      }

      quill.on('text-change', (delta: DeltaStatic, oldDelta: DeltaStatic, source: Sources) => {
        onTextChangeRef.current?.(delta, oldDelta, source)
      })

      quill.on('selection-change', (range: RangeStatic, oldRange: RangeStatic, source: Sources) => {
        onSelectionChangeRef.current?.(range, oldRange, source)
      })

      return () => {
        if (ref && 'current' in ref) {
          ;(ref as MutableRefObject<Quill | null>).current = null
        }
        container.innerHTML = ''
      }
    }, [ref])

    return (
      <Box className={classes.container}>
        <div ref={containerRef} className={classes.quillEditor}></div>
      </Box>
    )
  }
)

RichTextEditor.displayName = 'RichTextEditor'

export default RichTextEditor
